import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1 } from "iconsax-react";
import { Button, ModalWrapper, Dropdown, DropdownItem, Prompt, PromptForm } from "@/shared/ui";
import { MoreIcon } from "@/shared/assets/icons";
import { formatDateForDisplay, formatMoneyKzt, extractErrorMessage } from "@/shared/utils";
import { useDeleteCompletionActMutation } from "../../../api";
import type { GphCompletionAct } from "../../../types";
import ActStatusPipeline from "./ActStatusPipeline";

interface Props {
  act: GphCompletionAct;
  locale: "ru" | "kk" | "en";
  onClose: () => void;
  onEdit?: (act: GphCompletionAct) => void;
}

const SIGNABLE_STATUSES = new Set(["pending_signing", "signing_pending"]);
const EDITABLE_STATUSES = new Set(["draft", "pending_review", "pending_signing", "signing_pending"]);

export default function ActDetailModal({ act, locale, onClose, onEdit }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const showSign = act.sigex_url && SIGNABLE_STATUSES.has(act.status);
  const canManage = EDITABLE_STATUSES.has(act.status);

  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteCompletionAct] = useDeleteCompletionActMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  async function handleDeleteConfirm() {
    if (deletingId !== null) return;
    setDeletingId(act.id);
    try {
      await deleteCompletionAct(act.id).unwrap();
      setConfirmDelete(false);
      setPrompt({
        title: t("calculations.actDetail.deleteSuccessTitle"),
        text: t("calculations.actDetail.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmDelete(false);
      setPrompt({
        title: t("calculations.modal.errorTitle"),
        text: extractErrorMessage(err, "calculations.actDetail.deleteError", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <ModalWrapper onClose={onClose} width="w-3xl">
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 aspect-square radius-xs surface-component-fill content-action-neutral flex items-center justify-center shrink-0">
                <DocumentText1 size={20} color="currentColor" />
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="text-display-2xs content-base-primary">
                  {t("calculations.actDetail.actNumber", { number: act.act_number })}
                </h3>
                <p className="text-body-regular-sm content-action-neutral">
                  {formatDateForDisplay(act.period_start_date)} — {formatDateForDisplay(act.period_end_date)}
                </p>
              </div>
            </div>
            {canManage && (
              <Dropdown open={menuOpen} onClose={() => setMenuOpen(false)} align="right">
                <Button
                  isIconButton
                  variant="tertiary"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="w-8! h-8! p-0! flex absolute right-10 items-center justify-center radius-xs content-base-secondary!">
                  <MoreIcon />
                </Button>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit?.(act);
                  }}>
                  {t("calculations.actDetail.edit")}
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(true);
                  }}>
                  {t("calculations.actDetail.delete")}
                </DropdownItem>
              </Dropdown>
            )}
          </div>

          <ActStatusPipeline status={act.status} isPaymentApproved={act.is_payment_approved} isPaid={act.is_paid} />

          {act.lines.length > 0 && (
            <div className="p-4 radius-md surface-component-fill flex flex-col gap-2">
              <p className="text-label-sm content-action-neutral">{t("calculations.actDetail.services")}</p>
              {act.lines.map((line, index) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between gap-3 pt-3 border-t surface-base-stroke">
                  <p className="text-body-bold-sm content-base-secondary">
                    {index + 1}. {line.description}
                    {line.project?.name || line.project_name
                      ? ` ${t("calculations.actDetail.byProject", { project_name: line.project?.name || line.project_name })}`
                      : ""}
                  </p>
                  <p className="text-body-regular-sm content-base-primary shrink-0">
                    {formatMoneyKzt(line.line_amount ?? 0, locale)}
                  </p>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 pt-3 border-t surface-base-stroke">
                <p className="text-body-bold-sm content-base-primary">{t("calculations.actDetail.total")}</p>
                <p className="text-body-bold-sm content-base-primary">{formatMoneyKzt(act.amount, locale)}</p>
              </div>
            </div>
          )}

          {/* {showSign && (
            <div className="flex items-center gap-3 pt-2">
              <Button variant="primary" size="md" onClick={() => window.open(act.sigex_url!, "_blank")}>
                {t("calculations.sign")}
              </Button>
            </div>
          )} */}
        </div>
      </ModalWrapper>

      {confirmDelete && (
        <PromptForm
          title={t("calculations.actDetail.deleteConfirmTitle")}
          text={t("calculations.actDetail.deleteConfirmText")}
          variant="error"
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDeleteConfirm}
          isLoading={deletingId !== null}
          confirmText={t("calculations.actDetail.deleteConfirm")}
          cancelText={t("calculations.modal.cancel")}
          namespace="EmployeePortal"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => {
            const wasSuccess = prompt.variant === "success";
            setPrompt(null);
            if (wasSuccess) onClose();
          }}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}

